import { useState } from 'react';
import { Header } from '../components/Header.js';
import { Footer } from '../components/Footer.js';
import { Button, LinkButton } from '../components/primitives/Button.js';
import { Card, Container } from '../components/primitives/Misc.js';
import {
  ADD_ONS,
  ENTERPRISE,
  FAQ,
  FEATURE_GROUPS,
  METERING,
  PWA,
  SERVICES,
  SERVICES_CREDIT_NOTE,
  TIERS,
  annualPrice,
  formatAmount,
  type FeatureValue,
  type Tier,
} from '../data/plans.js';
import { PWA_URL } from '../config.js';
import type { useCurrency } from '../hooks/useCurrency.js';
import './PricingPage.css';

export interface PricingPageProps {
  currency: ReturnType<typeof useCurrency>;
  onGoHome: () => void;
  onGoDocs: () => void;
  onGoPricing: () => void;
  onGoLegal: () => void;
  onOpenAudit: () => void;
  onOpenBooking: () => void;
  onToggleHelp: () => void;
}

type Billing = 'monthly' | 'annual';

/** Renders a matrix cell. A bare glyph is not an accessible value, so the mark
 *  always carries a visually-hidden word alongside it. */
function Cell({ value }: { value: FeatureValue }) {
  if (value === true) {
    return (
      <span className="land-pp__yes">
        ✓<span className="land-sr">Included</span>
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="land-pp__no">
        —<span className="land-sr">Not included</span>
      </span>
    );
  }
  return <span className="land-pp__text">{value}</span>;
}

/**
 * `prefix` is separated from `amount` so "from" can render small. Inlined at the
 * price's own 26px it both wraps the unit onto a second line and makes
 * Enterprise look like the largest number on the page, which is the opposite of
 * what a floor price should communicate.
 */
function priceFor(
  tier: Tier,
  billing: Billing,
  symbol: string
): { prefix: string; amount: string; unit: string } {
  if (tier.price === null) return { prefix: '', amount: 'Custom', unit: '' };
  /* ⚠️ A `price === 0` arm lived here for the free-audit tier and is gone with it: no tier
     is zero-priced now, so it was a branch no test could reach, and the next reader would
     have taken its presence as evidence the ladder still has a free rung. `plans.test.ts`
     asserts the absence, so restoring a $0 tier fails there rather than rendering here. */
  const prefix = tier.from ? 'from' : '';
  if (billing === 'annual') {
    return { prefix, amount: `${symbol}${formatAmount(annualPrice(tier.price))}`, unit: '/yr' };
  }
  return { prefix, amount: `${symbol}${formatAmount(tier.price)}`, unit: '/mo' };
}

/**
 * The full pricing surface. The landing page carries the two self-serve prices;
 * everything that needs a table lives here.
 *
 * The local-mode block is deliberately its own section rather than a matrix
 * column. Every other column difference is a commercial choice we could reverse
 * tomorrow; the local limits follow from running in a browser, so listing them
 * beside "SSO ✓/✗" would imply they are ours to sell.
 *
 * ⚠️ **The free audit is not a column either, and was one until 2026-08-10** — card 1 of
 * 5 and a cell in all 21 comparison rows, which is a lot of table saying what a free
 * read-only run does not include. It is a motion, not a rung; `FREE_AUDIT` in
 * data/plans.ts describes it. **No CTA was lost here**: this page's `Header` already
 * takes `onOpenAudit` and renders the "Free data audit" button, so a prospect who
 * decides against a plan still has the audit one click away in the same viewport.
 */
export function PricingPage({
  currency,
  onGoHome,
  onGoDocs,
  onGoPricing,
  onGoLegal,
  onOpenAudit,
  onOpenBooking,
  onToggleHelp,
}: PricingPageProps) {
  const [billing, setBilling] = useState<Billing>('monthly');
  const symbol = currency.symbol;
  const withSymbol = (text: string) => text.replace(/\{sym\}/g, symbol);

  /* ⚠️ No tier carries `ctaAction: 'pwa'` — the sandbox is not a priced tier, it is free on
     every plan, so it belongs in the local-mode section below rather than in this ladder.
     `'pwa'` remains in the `PlanCtaAction` union and this map must NOT grow a branch for it:
     the fallthrough would put the audit lead-form in front of the one thing that needs no
     account, which is exactly the defect being removed from the two cards on the home page. */
  const ctaFor = (tier: Tier) => (tier.ctaAction === 'booking' ? onOpenBooking : onOpenAudit);

  return (
    <section data-screen-label="Pricing" >
      <Header
        view="pricing"
        ccyCode={currency.code}
        regionLabel={currency.regionLabel}
        onCycleCurrency={currency.cycle}
        onGoHome={onGoHome}
        onGoDocs={onGoDocs}
        onOpenAudit={onOpenAudit}
        onOpenBooking={onOpenBooking}
        onToggleHelp={onToggleHelp}
      />

      <Container className="land-pp">
        <h1 className="land-pp__h1">Plans and pricing</h1>
        <p className="land-pp__lede">
          We meter records under management — the live records we track. Never per API call, never per
          sync, never per duplicate found. Shown in {currency.name}, excluding {currency.taxLabel}.
        </p>

        <div className="land-pp__billing" role="group" aria-label="Billing period">
          <button
            type="button"
            className={`land-pp__billing-option${billing === 'monthly' ? ' land-pp__billing-option--on' : ''}`}
            aria-pressed={billing === 'monthly'}
            onClick={() => setBilling('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`land-pp__billing-option${billing === 'annual' ? ' land-pp__billing-option--on' : ''}`}
            aria-pressed={billing === 'annual'}
            onClick={() => setBilling('annual')}
          >
            Annual
            <span className="land-pp__billing-save">2 months free</span>
          </button>
        </div>

        {/* ---- Plan summary ---- */}
        <div className="land-pp__plans">
          {TIERS.map((tier) => {
            const price = priceFor(tier, billing, symbol);
            return (
              <Card
                key={tier.id}
                tone={tier.featured ? 'accent' : 'default'}
                className={`land-pp__plan${tier.featured ? ' land-pp__plan--featured' : ''}`}
              >
                <div className="land-pp__plan-name">{tier.name}</div>
                <div className="land-pp__plan-price">
                  {price.prefix && <span className="land-pp__plan-prefix">{price.prefix}</span>}
                  <span className="land-pp__plan-amount">{price.amount}</span>
                  {price.unit && <span className="land-pp__plan-unit">{price.unit}</span>}
                </div>
                <div className="land-pp__plan-blurb">{tier.blurb}</div>
                <ul className="land-pp__plan-facts">
                  <li>{tier.systems}</li>
                  <li>{tier.ceiling}</li>
                  {/* ADR-068: one D1 per (client, region), count gated by plan. For a
                      client with an EU Salesforce and a US HubSpot this is the deciding
                      difference between tiers, so it belongs in the facts list rather
                      than only in the add-on table further down. */}
                  <li>{tier.regions}</li>
                  <li>{tier.cadence}</li>
                  <li>{tier.users}</li>
                </ul>
                <Button
                  variant={tier.featured ? 'inverse' : 'secondary'}
                  size="md"
                  className="land-pp__plan-cta"
                  onClick={ctaFor(tier)}
                >
                  {tier.ctaLabel}
                </Button>
              </Card>
            );
          })}
        </div>

        {/* ---- The matrix ---- */}
        <h2 className="land-pp__h2">Everything in each plan</h2>
        <div className="land-pp__table-wrap">
          <table className="land-pp__table">
            <caption className="land-sr">
              Feature comparison across the Starter, Growth, Scale and Enterprise plans
            </caption>
            <thead>
              <tr>
                <th scope="col" className="land-pp__th-feature">
                  Feature
                </th>
                {TIERS.map((tier) => (
                  <th scope="col" key={tier.id} className={tier.featured ? 'land-pp__th--on' : undefined}>
                    {tier.name}
                  </th>
                ))}
              </tr>
            </thead>
            {FEATURE_GROUPS.map((group) => (
              <tbody key={group.label}>
                <tr className="land-pp__group">
                  <th scope="colgroup" colSpan={TIERS.length + 1}>
                    {group.label}
                  </th>
                </tr>
                {group.rows.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    {TIERS.map((tier) => (
                      <td key={tier.id} className={tier.featured ? 'land-pp__td--on' : undefined}>
                        <Cell value={row.values[tier.id]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>

        {/* ---- Add-ons ---- */}
        <h2 className="land-pp__h2">Add-ons</h2>
        <p className="land-pp__section-lede">
          Priced as a percentage of your plan, so they cost what your account is worth rather than a
          flat fee that punishes the smallest customer.
        </p>
        <div className="land-pp__addons">
          {ADD_ONS.map((addOn) => (
            <Card key={addOn.name} className="land-pp__addon">
              <div className="land-pp__addon-head">
                <span className="land-pp__addon-name">{addOn.name}</span>
                <span className="land-pp__addon-uplift">{withSymbol(addOn.uplift)}</span>
              </div>
              <ul className="land-checklist land-pp__addon-list">
                {addOn.unlocks.map((item) => (
                  <li key={item}>
                    <span className="land-checklist__mark">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <div className="land-pp__addon-avail">{addOn.availableOn}</div>
            </Card>
          ))}
        </div>

        {/* ---- Local mode ---- */}
        <h2 className="land-pp__h2" id="local-mode">
          Local-first mode, free on every plan
        </h2>
        <p className="land-pp__section-lede">
          {/* ⚠️ "compiled to WebAssembly" described software we did not build — the shipped engine is
              TypeScript in a Web Worker (B-168b). The claim worth keeping is that it is the SAME
              engine, which is true and is the reason this tier is credible at all. */}
          The same match engine, running on your own machine. It is how you
          try Dedupr without granting anything, and it stays available whatever you go on to pay.
        </p>
        {/* ⚠️ This section claimed to be "how you try Dedupr without granting anything" and then
            offered no way to try it — the whole pricing page had no route into the sandbox at
            all, because it is not a priced tier and so has no card in the ladder above. */}
        <LinkButton variant="tint" size="md" href={PWA_URL} className="land-pp__pwa-cta">
          Open the local sandbox
        </LinkButton>
        <div className="land-pp__pwa">
          <Card className="land-pp__pwa-card">
            <div className="land-mono-label">what it does</div>
            <ul className="land-checklist land-pp__pwa-list">
              {PWA.whatItDoes.map((item) => (
                <li key={item}>
                  <span className="land-checklist__mark">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </Card>
          <Card className="land-pp__pwa-card land-pp__pwa-card--limits">
            <div className="land-mono-label land-pp__pwa-limits-label">what needs the cloud, by design</div>
            <dl className="land-pp__limits">
              {PWA.notByDesign.map((entry) => (
                <div key={entry.limit} className="land-pp__limit">
                  <dt>{entry.limit}</dt>
                  <dd>{entry.why}</dd>
                </div>
              ))}
            </dl>
            <p className="land-pp__pwa-foot">
              When you reach one of these, the app tells you what it would take to go further and what
              you are missing by not — it never just greys a button out.
            </p>
          </Card>
        </div>

        {/* ---- Enterprise ---- */}
        <h2 className="land-pp__h2">Enterprise</h2>
        <p className="land-pp__section-lede">
          Quoted per deal, from {symbol}
          {formatAmount(4000)} a month. Everything in Scale, plus the things a security review asks
          for.
        </p>
        <dl className="land-pp__enterprise">
          {ENTERPRISE.map((entry) => (
            <div key={entry.capability} className="land-pp__ent-item">
              <dt>{entry.capability}</dt>
              <dd>{entry.description}</dd>
            </div>
          ))}
        </dl>
        <Button variant="primary" size="lg" className="land-pp__ent-cta" onClick={onOpenBooking}>
          Talk to sales
        </Button>

        {/* ---- Services ---- */}
        <h2 className="land-pp__h2">Prefer we did the first pass with you?</h2>
        <div className="land-pp__services">
          {SERVICES.map((service) => (
            <Card key={service.name} tone="tint">
              <div className="land-pp__service-head">
                <span className="land-pp__service-name">{service.name}</span>
                <span className="land-pp__service-price">
                  {symbol}
                  {formatAmount(service.price)}
                </span>
              </div>
              <p className="land-pp__service-body">{service.description}</p>
            </Card>
          ))}
        </div>
        <p className="land-pp__credit">{SERVICES_CREDIT_NOTE}</p>

        {/* ---- Metering ---- */}
        <h2 className="land-pp__h2">What we meter, and what we deliberately don’t</h2>
        <ul className="land-checklist land-pp__metering">
          {METERING.map((rule) => (
            <li key={rule}>
              <span className="land-checklist__mark">·</span>
              {withSymbol(rule)}
            </li>
          ))}
        </ul>

        {/* ---- FAQ ---- */}
        <h2 className="land-pp__h2">Questions people actually ask</h2>
        <dl className="land-pp__faq">
          {FAQ.map((item) => (
            <div key={item.question} className="land-pp__faq-item">
              <dt>{item.question}</dt>
              <dd>{withSymbol(item.answer)}</dd>
            </div>
          ))}
        </dl>
      </Container>

      <Footer onGoDocs={onGoDocs} onGoPricing={onGoPricing} onGoLegal={onGoLegal} />
    </section>
  );
}
